import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { Server, Plus, ArrowLeft, Wifi, AlertCircle, Check, X, RefreshCw } from 'lucide-react';

const RoutersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [licenseData, setLicenseData] = useState(null);
  const [routers, setRouters] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRouterCreds, setNewRouterCreds] = useState(null);
  const [testingConnection, setTestingConnection] = useState({});
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const [licenseRes, myRoutersRes, configuredRoutersRes] = await Promise.all([
        api.get('/api/license/current-pricing'),
        api.get('/api/licenses/my-routers'),
        api.get('/api/routers')
      ]);
      
      setLicenseData(licenseRes.data);
      
      // Combinar routers: usar datos completos de routers configurados
      // y agregar routers pendientes de la licencia
      const myRoutersData = myRoutersRes.data.routers || [];
      const configuredRouters = configuredRoutersRes.data || [];
      
      const combined = myRoutersData.map(myRouter => {
        if (myRouter.router_configured) {
          // Buscar datos completos del router configurado
          const fullRouter = configuredRouters.find(r => 
            r.ip_address === myRouter.internal_ip
          );
          
          if (fullRouter) {
            // Usar datos completos + marcar como configurado
            return {
              ...fullRouter,
              router_configured: true,
              router_index: myRouter.router_index
            };
          }
        }
        
        // Router pendiente - usar datos de la licencia
        return {
          ...myRouter,
          router_configured: false,
          isPending: true,
          name: myRouter.router_name
        };
      });
      
      setRouters(combined);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTestConnection = async (router) => {
    try {
      setTestingConnection(prev => ({ ...prev, [router.id]: true }));
      
      const response = await api.post(`/api/routers/${router.id}/test-connection`);
      
      if (response.data.connected) {
        alert('✅ Hay conexión\n\n' + response.data.message);
      } else {
        alert('❌ No hay conexión\n\n' + response.data.message);
      }
      
    } catch (error) {
      alert('❌ No hay conexión\n\nError: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestingConnection(prev => ({ ...prev, [router.id]: false }));
    }
  };
  
  const handleReconnect = (router) => {
    navigate('/reconectar-step1', { 
      state: { 
        router: router
      } 
    });
  };
  
  const handleConfigurePending = (router) => {
    navigate('/router-step1-script', { 
      state: { 
        pendingRouter: {
          router_index: router.router_index,
          vpn_internal_ip: router.internal_ip,
          vpn_external_port: router.vpn_external_port || router.vpn_port,
          vpn_username: router.vpn_username,
          vpn_password: router.password || router.mikrotik_password,
          license_id: licenseData?.license?.id
        }
      } 
    });
  };
  
  const handleAddRouter = async () => {
    try {
      setAdding(true);
      const response = await api.post('/api/routers/add-router');
      
      setNewRouterCreds(response.data.router);
      await loadData();
      
      alert(`✅ Router #${response.data.router.router_index} creado. Nuevo total: $${response.data.new_monthly_cost}/mes`);
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Error agregando router');
    } finally {
      setAdding(false);
      setShowAddModal(false);
    }
  };
  
  const getNextRouterIndex = () => {
    return (licenseData?.routers_count || 0) + 1;
  };
  
  const getExtraCost = () => {
    const nextIndex = getNextRouterIndex();
    return nextIndex > 1 ? 50 : 0;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  const hasRouters = routers && routers.length > 0;
  const extraCost = getExtraCost();
  const nextIndex = getNextRouterIndex();
  
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              📡 Mis Routers
            </h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {routers.length} router{routers.length !== 1 ? 'es' : ''} configurado{routers.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        
        {/* Lista de Routers - NUEVO DISEÑO */}
        {hasRouters ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {routers.map((router) => {
              const isPending = !router.router_configured;
              const colors = isPending
                ? { border: 'border-yellow-500', badge: 'bg-yellow-500', text: 'text-yellow-600' }
                : { border: 'border-green-500', badge: 'bg-green-500', text: 'text-green-600' };
              
              return (
                <div
                  key={router.id}
                  className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border-2 ${colors.border} relative`}
                >
                  {/* Badge de estado */}
                  {isPending && (
                    <div className="absolute top-2 right-2 z-10 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                      ⏳ PENDIENTE
                    </div>
                  )}
                  
                  {/* Header de la tarjeta */}
                  <div className={`${colors.badge} text-white text-xs font-semibold px-4 py-2`}>
                    Router #{router.router_index || '?'}
                  </div>
                  
                  <div className="p-6">
                    {/* Título */}
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {router.name || `Router ${router.router_index || '?'}`}
                      </h3>
                    </div>
                    
                    {/* Información */}
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">🌐</span>
                        <span className="font-semibold text-gray-700">IP: {router.ip_address || router.internal_ip}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">🔌</span>
                        <span className="font-semibold text-gray-700">Puerto: {router.port || router.vpn_external_port || '8728'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">👤</span>
                        <span className="font-semibold text-gray-700">Usuario: {router.username || 'ETWEB'}</span>
                      </div>
                    </div>
                    
                    {/* Botones de Acción */}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => handleTestConnection(router)}
                        disabled={testingConnection[router.id]}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-semibold text-sm"
                      >
                        {testingConnection[router.id] ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Probando...
                          </>
                        ) : (
                          <>
                            <Wifi className="w-4 h-4" />
                            Probar Conexión
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => isPending ? handleConfigurePending(router) : handleReconnect(router)}
                        className={`flex-1 px-4 py-2 ${isPending ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-600 hover:bg-orange-700'} text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-semibold text-sm`}
                      >
                        <RefreshCw className="w-4 h-4" />
                        {isPending ? 'Re-configurar' : 'Reconectar'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center mb-6">
            <Server className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-bold mb-2 text-gray-900">
              No tienes routers configurados
            </h3>
            <p className="text-gray-600">
              Configura tu primer router para empezar a usar el sistema
            </p>
          </div>
        )}
        
        {/* Botón Agregar Router */}
        <button
          onClick={() => setShowAddModal(true)}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-xl font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
        >
          <Plus className="w-6 h-6" />
          Configurar Router {nextIndex}
          {extraCost > 0 && ` (+$${extraCost}/mes)`}
        </button>
        
        {/* Modal de Confirmación */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Agregar Router #{nextIndex}
                </h2>
                <button onClick={() => setShowAddModal(false)} className="p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {extraCost > 0 ? (
                <>
                  <div className="rounded-lg p-4 mb-4 bg-blue-50">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Routers actuales:</span>
                        <span className="font-bold">{nextIndex - 1} (1 incluido)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Costo actual:</span>
                        <span className="font-bold">${licenseData?.pricing?.final_monthly_total || 0}/mes</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Router adicional:</span>
                        <span className="font-bold text-blue-600">+${extraCost}/mes</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-blue-200">
                        <span className="font-bold">Nuevo total:</span>
                        <span className="font-bold text-blue-600 text-xl">${(licenseData?.pricing?.final_monthly_total || 0) + extraCost}/mes</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-4 rounded-lg mb-6 bg-orange-50 border border-orange-200">
                    <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-orange-600">
                      Este cargo se aplicará en tu próximo pago
                    </p>
                  </div>
                </>
              ) : (
                <p className="mb-6 text-gray-700">
                  Tu primer router está incluido en el plan. Procede a configurarlo.
                </p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  disabled={adding}
                  className="flex-1 px-4 py-3 rounded-lg font-semibold bg-gray-200 hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddRouter}
                  disabled={adding}
                  className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                  {adding ? 'Creando...' : 'Agregar Router'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal de Credenciales Nuevas */}
        {newRouterCreds && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">
                ✅ Router #{newRouterCreds.router_index} Creado
              </h2>
              
              <div className="rounded-lg p-4 mb-4 bg-blue-50">
                <h3 className="font-bold mb-2">Credenciales VPN:</h3>
                <div className="space-y-2 font-mono text-sm">
                  <p>Dirección: {newRouterCreds.vpn_address}</p>
                  <p>Usuario: {newRouterCreds.vpn_username}</p>
                  <p>Contraseña: {newRouterCreds.vpn_password}</p>
                  <p>IP Interna: {newRouterCreds.internal_ip}</p>
                </div>
              </div>
              
              <p className="text-sm mb-4 text-gray-600">
                Configura tu router con estas credenciales. Puedes verlas en cualquier momento en "Credenciales VPN".
              </p>
              
              <button
                onClick={() => {
                  navigate('/router-step1-script', {
                    state: {
                      router: newRouterCreds
                    }
                  });
                  setNewRouterCreds(null);
                }}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Ir a Configuración del Router
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutersPage;
